import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components/native';
import { Box } from '@/components/base/box';
import { ListRenderItemInfo, Modal, Platform, Pressable } from 'react-native';
import { AppListView, AppListViewProps } from '@/components/base/app.list.view';
import { Chat, ChatTypeEnum } from '@matap/shared';
import { ChatView } from '@/components/compose/chat/chat.view';
import { ChatMenu, menuEmitter } from '@/components/compose/chat/chat.menu';
import { useScreenSize } from '@/hooks/use.screen.size';
import { useUser } from '@/hooks/use.user';
import { chatVisibilityEmitter } from '@/hooks/use.file.asset';

interface Props extends Omit<AppListViewProps<Chat>, 'renderItem'> {
  temporary: boolean;
  onEditChat?: (chat: Chat) => void;
}

function BaseChatList({ temporary, onEditChat, style, ...rest }: Props) {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const menuChatRef = useRef<Chat | null>(null);

  // ✅ 1. Destructure width from your screen size hook
  const { height: SCREEN_HEIGHT, width: SCREEN_WIDTH } = useScreenSize();
  const { userId } = useUser();

  const onViewableItemsChanged = useRef(({ changed }: any) => {
    // chatVisibilityEmitter.emit('change',changed.filter(c => c.isViewable).map(c => c.item._id))
    //console.log('visible',changed.filter(c => c.isViewable).map(c => c.item._id));
    changed.forEach((v: any) => {
      chatVisibilityEmitter.emit('change', v.item._id, v.isViewable);
      /*   if (v.isViewable) {

         const chat: Chat = v.item;
          if (chat.type === ChatTypeEnum.FILE && !chat.localFile && chat.file) {
            const instance = FileAsset.getInstance(chat.file, temporary);
            if (instance) {
              instance.get(true);
            } else {
              FileAsset.create(chat.file, temporary).then((asset) => asset.get(true));
            }
          }
      }*/
    });
  }).current;

  const viewabilityConfig = useRef({
    itemVisiblePercentThreshold: 1,
    minimumViewTime: 100,
  }).current;

  const onShowMenu = useCallback(
    (chat: Chat, pageX: number, pageY: number) => {
      if (!onEditChat) return;
      menuChatRef.current = chat;
      menuEmitter.emit('change', chat._id);

      const MENU_HEIGHT = 150;
      const MENU_WIDTH = 200; // Estimated max width of your ChatMenu
      const MARGIN = 16; // Safe padding from screen edges

      // Y Axis Math
      let safeY = pageY - 40;
      if (safeY < 50) safeY = 50;
      if (safeY > SCREEN_HEIGHT - MENU_HEIGHT - MARGIN) safeY = SCREEN_HEIGHT - MENU_HEIGHT - MARGIN;

      // ✅ 3. X Axis Math
      let safeX = pageX;
      // If tapping too close to left edge
      if (safeX < MARGIN) safeX = MARGIN;
      // If tapping too close to right edge
      if (safeX > SCREEN_WIDTH - MENU_WIDTH - MARGIN) safeX = SCREEN_WIDTH - MENU_WIDTH - MARGIN;

      setMenuPos({ x: safeX, y: safeY });
    },
    [SCREEN_HEIGHT, SCREEN_WIDTH, onEditChat],
  );

  const onCloseMenu = useCallback(() => {
    menuEmitter.emit('change', null);
    setMenuPos(null);
  }, []);

  const render = useCallback(
    ({ item: chat }: ListRenderItemInfo<Chat>) => <ChatView chat={chat} temporary={temporary} isSentByUser={chat.senderId === userId} onShowMenu={onShowMenu} />,
    [temporary, userId, onShowMenu],
  );

  const keyExtractor = useCallback((chat: Chat) => chat._id, []);

  const getItemType = useCallback(
    (chat: Chat) => {
      const type = chat.type === ChatTypeEnum.TEXT ? ChatTypeEnum.TEXT : (chat.localFile || chat.file?.metadata)?.type;
      const isSender = chat.senderId === userId;
      return `${type}-${isSender}`;
    },
    [userId],
  );

  return (
    <Wrapper style={style}>
      <AppListView
        initialNumToRender={15}
        maxToRenderPerBatch={15}
        windowSize={21}
        removeClippedSubviews={Platform.OS !== 'web'}
        updateCellsBatchingPeriod={50}
        inverted
        keyExtractor={keyExtractor}
        renderItem={render}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        {...rest}
      />

      {menuPos && (
        <Modal transparent visible={true} animationType="fade" onRequestClose={onCloseMenu}>
          <ModalContainer>
            <OverlayBackground onPress={onCloseMenu} />
            {/* ✅ 4. Pass the calculated left position */}
            <MenuPosition pointerEvents={'box-none'} top={menuPos.y} left={menuPos.x}>
              <ChatMenu chat={menuChatRef.current!} onClose={onCloseMenu} onEditChat={onEditChat} />
            </MenuPosition>
          </ModalContainer>
        </Modal>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(Box)`
  flex: 1;
  position: relative;
`;

const ModalContainer = styled(Box)`
  flex: 1;
`;

const OverlayBackground = styled(Pressable)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const MenuPosition = styled(Box)<{ top: number; left: number }>`
  position: absolute;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
`;

export const ChatList = React.memo(BaseChatList);
